import { useMemo, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import PlaylistAdd from '@mui/icons-material/PlaylistAdd';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { TbReorder } from 'react-icons/tb';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';

const outlinedTriggerSx = {
  'minWidth': 0,
  'px': 0.75,
  'py': 0.25,
  'lineHeight': 1,
  '&[aria-disabled="true"]': {
    cursor: 'default',
  },
} as const;

const COMPACT_ICON_PX = 20;
const NARROW_ICON_PX = 24;

const iconBoxSx = (size: number) =>
  ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    fontSize: size,
    lineHeight: 1,
  }) as const;

const narrowTriggerIconSx = {
  'padding': '2px',
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  '&[aria-disabled="true"]': {
    cursor: 'default',
  },
  '& > svg, & .permission-menu-icon': {
    fontSize: `${NARROW_ICON_PX}px`,
    width: `${NARROW_ICON_PX}px`,
    height: `${NARROW_ICON_PX}px`,
  },
} as const;

const lightSecondarySwitchSx = (theme: Theme) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    'color': alpha(theme.palette.secondary.main, 0.72),
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.06),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.secondary.main,
    opacity: 0.28,
  },
});

function resolvePermissionIconColor(theme: Theme, allowed: boolean, locked: boolean): string {
  if (!allowed && locked) {
    return theme.palette.action.disabled;
  }
  if (!allowed && !locked) {
    return theme.palette.action.active;
  }
  if (allowed && locked) {
    return alpha(theme.palette.secondary.main, 0.55);
  }
  return theme.palette.secondary.main;
}

function resolveLockedReasonSuffix(reason: string | undefined, readOnlySuffix: string): string {
  if (reason?.trim()) {
    return `: ${reason.trim()}`;
  }
  return readOnlySuffix;
}

type PlaylistMediaPermissionMenuProps = {
  field: 'allowSelfCreate' | 'allowReorder';
  labelPrefix: 'hosts' | 'speakers';
  allowed: boolean;
  canEdit: boolean;
  isPending?: boolean;
  lockedReason?: string;
  testId: string;
  onSelect: (allowed: boolean) => void;
  /** Wide layout: outlined icon button to match Request/Create; narrow: plain icon button. */
  useOutlinedTrigger?: boolean;
};

export function PlaylistMediaPermissionMenu({
  field,
  labelPrefix,
  allowed,
  canEdit,
  isPending = false,
  lockedReason,
  testId,
  onSelect,
  useOutlinedTrigger = false,
}: PlaylistMediaPermissionMenuProps) {
  const theme = useTheme();
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const popoverOpen = Boolean(anchorEl);
  const locked = !canEdit;
  const iconSize = useOutlinedTrigger ? COMPACT_ICON_PX : NARROW_ICON_PX;
  const iconColor = resolvePermissionIconColor(theme, allowed, locked);
  const roleLabel = t(`media.settings.playlist.${labelPrefix}.role` as TranslationKey);
  const readOnlySuffix = t('media.settings.playlist.menu.lockedReadOnlySuffix' as TranslationKey);
  const fieldKey = `media.settings.playlist.menu.fields.${field}` as TranslationKey;

  const { title, intro, switchLabel } = useMemo(() => {
    const reasonSuffix = resolveLockedReasonSuffix(lockedReason, readOnlySuffix);
    return {
      title: t(`${fieldKey}.title` as TranslationKey),
      intro: locked
        ? t(`${fieldKey}.introLocked` as TranslationKey, { role: roleLabel, reasonSuffix })
        : t(`${fieldKey}.intro` as TranslationKey, { role: roleLabel }),
      switchLabel: t(`${fieldKey}.switchLabel` as TranslationKey),
    };
  }, [fieldKey, locked, lockedReason, readOnlySuffix, roleLabel, t]);

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isPending) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const icon =
    field === 'allowSelfCreate' ? (
      <PlaylistAdd sx={{ ...iconBoxSx(iconSize), color: iconColor }} />
    ) : (
      <Box className="permission-menu-icon" component="span" sx={{ ...iconBoxSx(iconSize), color: iconColor }}>
        <TbReorder size={iconSize} />
      </Box>
    );

  const triggerProps = {
    'aria-label': title,
    'aria-haspopup': 'dialog' as const,
    'aria-expanded': popoverOpen,
    'aria-disabled': locked || isPending,
    'onClick': handleTriggerClick,
    'data-testid': testId,
  };

  const trigger = useOutlinedTrigger ? (
    <Button
      {...triggerProps}
      variant="outlined"
      size="small"
      color="secondary"
      disabled={isPending}
      sx={outlinedTriggerSx}
    >
      {icon}
    </Button>
  ) : (
    <IconButton {...triggerProps} size="small" disabled={isPending} sx={narrowTriggerIconSx}>
      {icon}
    </IconButton>
  );

  return (
    <>
      <Tooltip title={title} disableInteractive disableTouchListener>
        <span>{trigger}</span>
      </Tooltip>
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(event) => event.stopPropagation()}
      >
        <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 220, maxWidth: 280 }} onClick={(event) => event.stopPropagation()}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {intro}
            </Typography>
          </Stack>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                size="small"
                color="secondary"
                checked={allowed}
                disabled={locked || isPending}
                onChange={(event) => {
                  const nextAllowed = event.target.checked;
                  if (nextAllowed !== allowed) {
                    onSelect(nextAllowed);
                  }
                }}
                sx={lightSecondarySwitchSx(theme)}
                data-testid={`${testId}-switch`}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                <Typography variant="body2" component="span">
                  {switchLabel}
                </Typography>
                {locked ? (
                  <LockOutlined fontSize="inherit" sx={{ fontSize: '1rem', color: 'action.disabled' }} aria-hidden />
                ) : null}
              </Stack>
            }
          />
        </Stack>
      </Popover>
    </>
  );
}
